import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ArrowLeft, BookOpen, FileText, List } from "lucide-react";
import { useGetList } from "ra-core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Markdown } from "../misc/Markdown";
import { MARKETING_PATHS } from "./mobile";

/**
 * The manual, readable inside the CRM.
 *
 * WHY THE DOCS COME FROM POSTGRES
 * -------------------------------
 * They are authored as markdown in barrel_scout_crm/docs/, which is the source
 * of truth: git-versioned, diffable, and editable by an agent with no app
 * running. But this app is built and deployed from the OTHER repo -- Cloudflare
 * builds atomic_crm alone and cannot reach into barrel_scout_crm -- so bundling
 * the markdown at build time would couple the two repos' builds.
 *
 * So the repo holds the source, `pipeline/sync_docs.py` publishes it into
 * `public.docs`, and this screen reads that.
 *
 * READ-ONLY, DELIBERATELY
 * -----------------------
 * There is no edit path and no RLS policy granting one. A write here would let
 * the published copy drift from git with no way to tell which was right.
 *
 * WHY PLAIN <aside> RATHER THAN shadcn's <Sidebar>
 * -----------------------------------------------
 * admin/layout.tsx already wraps the whole app in a <SidebarProvider>. That
 * provider owns one open/closed state, a cookie, and a mobile sheet, so a
 * second <Sidebar> nested inside it does not compose -- it competes for the
 * same context and would toggle with the app's own nav. Two sticky <aside>
 * elements are ~20 lines of Tailwind, own their own state, and cannot fight
 * the shell.
 *
 * WHY STICKY COLUMNS AND A VIEWPORT-ROOTED OBSERVER
 * -------------------------------------------------
 * admin/layout.tsx marks <main> `h-svh`, which reads as a viewport-height
 * shell with its own inner scroller -- so this first gave each column its own
 * `overflow-y-auto`. Measuring the running app says otherwise: <main> computes
 * to the full content height (5604px on a long document), so nothing up the
 * chain constrains height, an inner `overflow-y-auto` never engages, and it is
 * the WINDOW that scrolls.
 *
 * So the columns are sticky against the page scroll, and the IntersectionObserver
 * is rooted at the viewport. The app header is not sticky -- it scrolls away
 * with the page -- so a small top offset is all that is needed.
 */

type Doc = {
  id: number;
  slug: string;
  title: string;
  summary: string | null;
  body: string;
  ord: number;
  source_path: string;
  updated_at: string;
};

type Heading = { id: string; text: string; level: number };

import { DOC_LIST_COLUMNS } from "./docsColumns";

/**
 * The navigation list — every document, WITHOUT its body.
 *
 * WHY THE COLUMN LIST IS NOT AN OPTIMIZATION DETAIL
 * --------------------------------------------------
 * This screen exists to be read on a phone. Selecting `*` here fetched every
 * document's full text on every visit -- measured at 322 kB across 11 docs,
 * including ~90 kB of archives nobody had opened -- to render a sidebar of
 * eleven titles. The cost grew with every word written into docs/, so the
 * documentation was quietly taxing the reader for existing.
 *
 * `meta.columns` is PostgREST vertical filtering, passed through by
 * ra-data-postgrest. The nav now costs a couple of kB and stays flat however
 * long the manual gets; `useDocBody` fetches exactly one body, on demand.
 *
 * KEEP `body` OUT OF THIS LIST. Adding it back is invisible in development and
 * silently restores the whole payload.
 */
const useDocs = () =>
  useGetList<Doc>("docs", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "ord", order: "ASC" },
    meta: { columns: DOC_LIST_COLUMNS },
  });

/**
 * The body of ONE document, fetched only when it is actually opened.
 *
 * A filtered getList rather than getOne, because the route carries a slug and
 * getOne needs the primary key -- looking the id up from the nav list first
 * would make the body wait on a request it does not otherwise need.
 */
const useDocBody = (slug?: string) =>
  useGetList<Doc>(
    "docs",
    {
      filter: { slug },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "ord", order: "ASC" },
    },
    { enabled: !!slug },
  );

/** An archive is anything numbered 90+. Kept, but visibly set apart. */
const isArchive = (d: Doc) => d.ord >= 90;

/** Strip the `.md` cross-links carry so they also resolve on GitHub. */
const bare = (s?: string) => s?.replace(/\.md$/, "");

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);

const Loading = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} className="h-16 w-full" />
    ))}
  </div>
);

/* ── left sidebar: the other documents ─────────────────────────────────── */

const DocsNav = ({ docs, current }: { docs: Doc[]; current?: string }) => {
  const Row = ({ d }: { d: Doc }) => (
    <Link
      key={d.id}
      to={`${MARKETING_PATHS.docs}/${d.slug}`}
      className={cn(
        "block rounded px-2 py-1.5 text-sm transition-colors",
        d.slug === current
          ? "bg-muted font-medium text-foreground"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      {d.title}
    </Link>
  );
  const current_ = docs.filter((d) => !isArchive(d));
  const archives = docs.filter(isArchive);

  return (
    <nav className="space-y-1">
      <Link
        to={MARKETING_PATHS.docs}
        className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold"
      >
        <BookOpen className="size-4" /> Docs
      </Link>
      {current_.map((d) => (
        <Row key={d.id} d={d} />
      ))}
      {archives.length ? (
        <>
          <div className="px-2 pt-3 pb-1 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
            Archive
          </div>
          {archives.map((d) => (
            <Row key={d.id} d={d} />
          ))}
        </>
      ) : null}
    </nav>
  );
};

/* ── right sidebar: this document ──────────────────────────────────────── */

const Toc = ({
  headings,
  active,
  onJump,
}: {
  headings: Heading[];
  active: string | null;
  onJump: (id: string) => void;
}) => (
  <nav className="space-y-1">
    <div className="px-2 pb-1 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
      On this page
    </div>
    {headings.map((h) => (
      <button
        key={h.id}
        type="button"
        onClick={() => onJump(h.id)}
        className={cn(
          "block w-full text-left rounded px-2 py-1 text-xs transition-colors",
          h.level === 3 && "pl-5",
          h.id === active
            ? "text-foreground font-medium bg-muted"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {h.text}
      </button>
    ))}
  </nav>
);

/* ── index ─────────────────────────────────────────────────────────────── */

export const DocsIndex = () => {
  const { data, isPending, error } = useDocs();
  const isMobile = useIsMobile();

  if (isPending) return <Loading />;
  if (error)
    return (
      <div className="text-sm text-destructive">
        Could not load the docs. If this persists, check that
        <code className="mx-1">sync_docs.py</code> has run.
      </div>
    );

  const docs = data ?? [];
  const current = docs.filter((d) => !isArchive(d));
  const archives = docs.filter(isArchive);

  const Card = ({ d }: { d: Doc }) => (
    <Link
      to={`${MARKETING_PATHS.docs}/${d.slug}`}
      className="flex items-start gap-3 border rounded-lg p-3 hover:bg-muted transition-colors"
    >
      <FileText className="size-5 shrink-0 mt-0.5 text-muted-foreground" />
      <div className="min-w-0">
        <div className="font-medium">{d.title}</div>
        {d.summary ? (
          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {d.summary}
          </div>
        ) : null}
      </div>
    </Link>
  );

  return (
    <div className="flex gap-8">
      {!isMobile && (
        <aside className="w-56 shrink-0 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto pl-1">
          <DocsNav docs={docs} />
        </aside>
      )}
      <div className="min-w-0 flex-1 max-w-3xl space-y-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="size-6" /> Docs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            The manual. Source of truth is <code>barrel_scout_crm/docs/</code>;
            this is the published copy and is read-only.
          </p>
        </div>
        <div className="space-y-2">
          {current.map((d) => (
            <Card key={d.id} d={d} />
          ))}
        </div>
        {archives.length ? (
          <div className="space-y-2 pt-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Archive — kept for reference, not current
            </div>
            {archives.map((d) => (
              <Card key={d.id} d={d} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ── one document ──────────────────────────────────────────────────────── */

export const DocsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data, isPending: navPending, error } = useDocs();

  const [headings, setHeadings] = useState<Heading[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [tocOpen, setTocOpen] = useState(false);
  // State, not a ref: the heading effect must re-run once the node exists.
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  const wanted = bare(slug);
  const docs = data ?? [];
  const doc = docs.find((d) => d.slug === wanted);

  // The body arrives separately, so the sidebar can paint before it lands.
  const {
    data: bodyRows,
    isPending: bodyPending,
    error: bodyError,
  } = useDocBody(wanted);
  const body = bodyRows?.[0]?.body ?? "";
  const isPending = navPending || bodyPending;

  /**
   * Memoized so ONLY a change of document re-renders it.
   *
   * <Markdown> renders through dangerouslySetInnerHTML, so every render of it
   * replaces the subtree and destroys the heading ids assigned below. Scroll-spy
   * updates `active` on nearly every scroll tick, so without this the ids were
   * being wiped and rebuilt continuously -- and the first symptom was 15
   * headings in the DOM with 0 ids on them.
   */
  const rendered = useMemo(() => <Markdown>{body}</Markdown>, [body]);

  /**
   * Build the table of contents from the RENDERED DOM, and re-apply the ids
   * after every render.
   *
   * Two things force this shape.
   *
   * The ids must agree with the elements they scroll to, and re-parsing the
   * markdown is how they drift: marked's heading text is not the raw source
   * text (inline code, links and entities are all transformed). Reading the
   * headings back off the DOM makes one pass the source of both id and label.
   *
   * And the ids do not survive on their own. <Markdown> renders through
   * dangerouslySetInnerHTML, so every re-render replaces the subtree and wipes
   * any attribute set imperatively. Doing this in a ref callback therefore
   * assigns ids exactly once and then loses them to the very re-render that
   * setHeadings triggers -- which is what happened: 15 headings, 0 ids.
   *
   * With the render memoized above the subtree is stable for a given document,
   * so this runs once per document. The state write is still guarded by
   * comparing the id list, so a re-run can never start a render loop.
   */
  useLayoutEffect(() => {
    if (!scrollRoot) return;
    const seen = new Map<string, number>();
    const found: Heading[] = [];
    scrollRoot.querySelectorAll("h2, h3").forEach((node) => {
      const text = node.textContent?.trim() ?? "";
      if (!text) return;
      let id = slugify(text);
      // Duplicate headings are common ("Verified", "Why this matters"), and
      // duplicate ids would silently scroll to the first one every time.
      const n = seen.get(id) ?? 0;
      seen.set(id, n + 1);
      if (n) id = `${id}-${n}`;
      node.id = id;
      found.push({ id, text, level: node.tagName === "H3" ? 3 : 2 });
    });
    const key = (hs: Heading[]) => hs.map((h) => h.id).join("|");
    setHeadings((prev) => (key(prev) === key(found) ? prev : found));
  }, [scrollRoot, doc?.body]);

  /** Highlight the heading nearest the top of the viewport. */
  useEffect(() => {
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      // A band across the upper third: a heading counts as "current" once it
      // reaches the top, not when it first appears at the bottom.
      // root: the viewport -- the window is what scrolls here.
      { rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings]);

  const jump = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActive(id);
    setTocOpen(false);
  }, []);

  /**
   * Keep internal links inside the SPA. marked emits a plain <a>, which would
   * otherwise trigger a full page load -- survivable, since Pages falls back to
   * index.html, but it throws away app state and flashes a reload on a phone.
   */
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href");
    if (!href || /^(https?:|mailto:)/.test(href)) return;
    e.preventDefault();
    if (href.startsWith("#")) return jump(href.slice(1));
    navigate(`${MARKETING_PATHS.docs}/${bare(href)}`);
  };

  if (isPending) return <Loading />;

  if (error || bodyError || !doc)
    return (
      <div className="space-y-3">
        <Button asChild variant="ghost" size="sm">
          <Link to={MARKETING_PATHS.docs}>
            <ArrowLeft className="size-4" /> All docs
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">
          No document called <code>{wanted}</code>. It may have been renamed —
          check the index.
        </p>
      </div>
    );

  return (
    <div className="flex gap-8">
      {!isMobile && (
        <aside className="w-56 shrink-0 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto pl-1">
          <DocsNav docs={docs} current={doc.slug} />
        </aside>
      )}

      <article
        ref={setScrollRoot}
        className="min-w-0 flex-1 max-w-3xl space-y-4"
      >
        {isMobile && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button asChild variant="ghost" size="sm">
              <Link to={MARKETING_PATHS.docs}>
                <ArrowLeft className="size-4" /> All docs
              </Link>
            </Button>
            {isArchive(doc) ? <Badge variant="outline">Archive</Badge> : null}
          </div>
        )}

        {/* On a phone there is no room for a third column, so the contents
            collapse into a disclosure above the document. */}
        {isMobile && headings.length > 2 && (
          <div className="border rounded-lg">
            <button
              type="button"
              onClick={() => setTocOpen((o) => !o)}
              className="flex w-full items-center gap-2 p-3 text-sm font-medium"
              aria-expanded={tocOpen}
            >
              <List className="size-4" /> On this page ({headings.length})
            </button>
            {tocOpen && (
              <div className="px-2 pb-2">
                <Toc headings={headings} active={active} onJump={jump} />
              </div>
            )}
          </div>
        )}

        {!isMobile && isArchive(doc) ? (
          <Badge variant="outline">Archive</Badge>
        ) : null}

        {/* Click delegation for links inside the rendered markdown. The anchors
            themselves stay real, focusable, keyboard-activatable links -- a
            keyboard activation fires this same click event -- so the wrapper
            adds no interaction that is not already reachable without it. */}
        <div onClick={onClick}>{rendered}</div>

        <div className="text-xs text-muted-foreground border-t pt-3">
          <code>{doc.source_path}</code> · published{" "}
          {new Date(doc.updated_at).toLocaleString()}
          <div className="mt-1">
            Edit the file in the repo and run <code>sync_docs.py</code>; this
            page is read-only.
          </div>
        </div>
      </article>

      {!isMobile && headings.length > 1 && (
        <aside className="w-52 shrink-0 hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto">
          <Toc headings={headings} active={active} onJump={jump} />
        </aside>
      )}
    </div>
  );
};
